const isDevelopment = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const SENTRY_BOT_API_URL = import.meta.env.VITE_SENTRY_BOT_API_URL ||
  (isDevelopment ? 'http://localhost:3001' : 'https://web-production-7d3e.up.railway.app');

const jsonHeaders = {
  'Content-Type': 'application/json'
};

const getAuthHeaders = (token?: string) => ({
  ...jsonHeaders,
  ...(token ? { Authorization: `Bearer ${token}` } : {})
});

const toBaseUnits = (value: string, decimals: number): string => {
  const trimmed = value.trim();
  if (!trimmed) return '0';
  const negative = trimmed.startsWith('-');
  const normalized = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = normalized.split('.');
  const safeWhole = whole.replace(/^0+(?=\d)/, '');
  const paddedFraction = (fraction + '0'.repeat(decimals)).slice(0, decimals);
  const combined = `${safeWhole || '0'}${paddedFraction}`.replace(/^0+(?=\d)/, '');
  const result = combined || '0';
  return negative ? `-${result}` : result;
};

export async function loginInvestorSession(investorName: string, password: string): Promise<{
  success: boolean;
  token?: string;
  expiresAt?: string;
  walletPublicKey?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/investor/pwa/login`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ investorName, password })
    });
    const result = await response.json();
    if (!response.ok || !result?.success) {
      return { success: false, error: result?.error || 'Failed to authenticate trading session' };
    }
    return {
      success: true,
      token: result.data?.token,
      expiresAt: result.data?.expiresAt,
      walletPublicKey: result.data?.walletPublicKey
    };
  } catch (error: any) {
    return { success: false, error: error?.message || 'Trading session unavailable' };
  }
}

export async function sellInvestorToken(params: {
  token: string;
  mintAddress: string;
  amount: string;
  decimals: number;
  minAmountOut?: string;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const amountIn = toBaseUnits(params.amount, params.decimals);
    if (!amountIn || amountIn === '0') {
      return { success: false, error: 'Amount must be greater than 0' };
    }

    const response = await fetch(`${SENTRY_BOT_API_URL}/api/investor/pwa/swap`, {
      method: 'POST',
      headers: getAuthHeaders(params.token),
      body: JSON.stringify({
        mintAddress: params.mintAddress,
        amountIn,
        minAmountOut: params.minAmountOut || '0'
      })
    });
    const result = await response.json();
    if (!response.ok || !result?.success) {
      return { success: false, error: result?.error || 'Swap failed' };
    }
    return { success: true, data: result.data };
  } catch (error: any) {
    return { success: false, error: error?.message || 'Swap failed' };
  }
}

export async function withdrawInvestorSol(params: {
  token: string;
  toAddress: string;
  amountSol?: string;
  withdrawAll?: boolean;
}): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/investor/pwa/withdraw-sol`, {
      method: 'POST',
      headers: getAuthHeaders(params.token),
      body: JSON.stringify({
        toAddress: params.toAddress,
        amountSol: params.amountSol,
        withdrawAll: params.withdrawAll ? 'true' : 'false'
      })
    });
    const result = await response.json();
    if (!response.ok || !result?.success) {
      return { success: false, error: result?.error || 'Withdraw failed' };
    }
    return { success: true, data: result.data };
  } catch (error: any) {
    return { success: false, error: error?.message || 'Withdraw failed' };
  }
}

export async function unwrapInvestorWsol(token: string): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/investor/pwa/unwrap-wsol`, {
      method: 'POST',
      headers: getAuthHeaders(token)
    });
    const result = await response.json();
    if (!response.ok || !result?.success) {
      return { success: false, error: result?.error || 'Unwrap failed' };
    }
    return { success: true, data: result.data };
  } catch (error: any) {
    return { success: false, error: error?.message || 'Unwrap failed' };
  }
}

export async function logoutInvestorSession(token: string): Promise<void> {
  try {
    await fetch(`${SENTRY_BOT_API_URL}/api/investor/pwa/logout`, {
      method: 'POST',
      headers: getAuthHeaders(token)
    });
  } catch {
    // Silent logout failure
  }
}
